/**
 * Emmet do Kompil (sem dependência): expande abreviações de HTML/JSX e de CSS em snippets do
 * CodeMirror, com os campos (`${}`) onde a pessoa ainda vai digitar.
 *
 * HTML: `!` (documento HTML5), `div`, `.classe`, `#id`, `[attr=valor]`, `{texto}`, `*3`, `$`
 * (numeração), `>` (filho), `+` (irmão), `^` (sobe um nível), `( )` (grupo) e atalhos como
 * `link:css`, `script:src`, `a:link`, `input:email`, `btn`.
 * CSS: `m10` → `margin: 10px;`, `w100p` → `width: 100%;`, `df` → `display: flex;`, `jcc`…
 */

export const HTML5_DOCUMENT = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
\t<meta charset="UTF-8">
\t<meta name="viewport" content="width=device-width, initial-scale=1.0">
\t<title>\${1:Documento}</title>
</head>
<body>
\t\${0}
</body>
</html>`;

const VOID = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

/** Atributos que já aparecem vazios (com campo para preencher). */
const DEFAULT_ATTRS: Record<string, string[]> = {
  a: ["href"],
  img: ["src", "alt"],
  input: ["type=text"],
  link: ["rel=stylesheet", "href"],
  form: ["action"],
  label: ["for"],
  iframe: ["src"],
  source: ["src"],
  video: ["src"],
  audio: ["src"],
  script: [],
  meta: [],
};

/** Atalhos ("tag:variante") como os do VS Code. */
const ALIASES: Record<string, { tag: string; attrs: string[] }> = {
  "link:css": { tag: "link", attrs: ["rel=stylesheet", "href=style.css"] },
  "link:favicon": { tag: "link", attrs: ["rel=icon", "type=image/x-icon", "href=favicon.ico"] },
  "script:src": { tag: "script", attrs: ["src"] },
  "script:module": { tag: "script", attrs: ["type=module", "src"] },
  "a:link": { tag: "a", attrs: ["href=https://"] },
  "a:mail": { tag: "a", attrs: ["href=mailto:"] },
  "input:text": { tag: "input", attrs: ["type=text", "name"] },
  "input:email": { tag: "input", attrs: ["type=email", "name"] },
  "input:password": { tag: "input", attrs: ["type=password", "name"] },
  "input:checkbox": { tag: "input", attrs: ["type=checkbox", "name"] },
  "input:radio": { tag: "input", attrs: ["type=radio", "name"] },
  "input:number": { tag: "input", attrs: ["type=number", "name"] },
  "input:submit": { tag: "input", attrs: ["type=submit", "value"] },
  "button:submit": { tag: "button", attrs: ["type=submit"] },
  btn: { tag: "button", attrs: [] },
  "meta:vp": { tag: "meta", attrs: ["name=viewport", "content=width=device-width, initial-scale=1.0"] },
};

/** Filho implícito de `.classe` sem tag, conforme o pai. */
const IMPLICIT_CHILD: Record<string, string> = {
  ul: "li",
  ol: "li",
  table: "tr",
  tbody: "tr",
  thead: "tr",
  tr: "td",
  select: "option",
  optgroup: "option",
  span: "span",
  em: "span",
  strong: "span",
  a: "span",
};

interface Node {
  tag: string;
  id?: string;
  classes: string[];
  attrs: Array<{ name: string; value: string | null }>;
  text?: string;
  count: number;
  children: Node[];
}

class ParseError extends Error {}

const blank = (): Node => ({ tag: "", classes: [], attrs: [], count: 1, children: [] });

/**
 * Lê a abreviação com uma pilha de níveis: `>` desce para o último elemento, `+` fica no mesmo
 * nível e cada `^` sobe um. Grupos `( )` são lidos à parte e podem repetir (`(li>a)*3`).
 */
function parse(abbr: string): Node[] {
  let i = 0;
  const peek = () => abbr[i];
  const word = () => {
    const m = /^[A-Za-z0-9_:\-$@]+/.exec(abbr.slice(i));
    if (!m) return "";
    i += m[0].length;
    return m[0];
  };
  const count = () => {
    if (peek() !== "*") return 1;
    i++;
    const m = /^\d+/.exec(abbr.slice(i));
    if (!m) throw new ParseError("repetição sem número");
    i += m[0].length;
    return Math.min(100, Number(m[0]));
  };
  /** Lê até o fim ou até o ")" do grupo atual. */
  const sequence = (): Node[] => {
    const root = blank();
    root.tag = "#root";
    const stack: Node[] = [root];
    let last: Node | null = null;
    const parentTag = () => (stack.length > 1 ? stack[stack.length - 1]!.tag : null);
    const add = (nodes: Node[]) => {
      stack[stack.length - 1]!.children.push(...nodes);
      last = nodes[nodes.length - 1] ?? last;
    };
    for (;;) {
      // Um elemento ou um grupo.
      if (peek() === "(") {
        i++;
        const group = sequence();
        if (peek() !== ")") throw new ParseError("grupo sem fechar");
        i++;
        const n = count();
        const out: Node[] = [];
        for (let k = 0; k < n; k++) out.push(...group.map((g) => clone(g, k + 1)));
        add(out);
      } else {
        add([element(parentTag())]);
      }
      // Operador seguinte.
      const op = peek();
      if (op === ">") {
        i++;
        if (!last) throw new ParseError("filho sem pai");
        stack.push(last);
      } else if (op === "+") {
        i++;
      } else if (op === "^") {
        while (peek() === "^") {
          i++;
          if (stack.length > 1) stack.pop();
        }
      } else break;
    }
    return root.children;
  };
  const element = (parent: string | null): Node => {
    const node = blank();
    node.tag = word();
    let any = Boolean(node.tag);
    for (;;) {
      const c = peek();
      if (c === ".") {
        i++;
        const cls = word();
        if (!cls) throw new ParseError("classe vazia");
        node.classes.push(cls);
        any = true;
      } else if (c === "#") {
        i++;
        const id = word();
        if (!id) throw new ParseError("id vazio");
        node.id = id;
        any = true;
      } else if (c === "[") {
        i++;
        const end = abbr.indexOf("]", i);
        if (end < 0) throw new ParseError("atributo sem fechar");
        for (const m of abbr.slice(i, end).matchAll(/([^\s=]+)(?:=(?:"([^"]*)"|'([^']*)'|(\S*)))?/g)) {
          node.attrs.push({ name: m[1]!, value: m[2] ?? m[3] ?? m[4] ?? null });
        }
        i = end + 1;
        any = true;
      } else if (c === "{") {
        i++;
        const end = abbr.indexOf("}", i);
        if (end < 0) throw new ParseError("texto sem fechar");
        node.text = abbr.slice(i, end);
        i = end + 1;
        any = true;
      } else break;
    }
    if (!any) throw new ParseError("elemento vazio");
    if (!node.tag) {
      const textOnly = node.text !== undefined && !node.classes.length && !node.id && !node.attrs.length;
      node.tag = textOnly ? "" : (parent && IMPLICIT_CHILD[parent]) || "div";
    }
    node.count = count();
    return node;
  };
  const out = sequence();
  if (i !== abbr.length) throw new ParseError("sobrou texto");
  return out;
}

function clone(n: Node, index: number): Node {
  return {
    ...n,
    id: n.id && number(n.id, index),
    classes: n.classes.map((c) => number(c, index)),
    attrs: n.attrs.map((a) => ({ name: a.name, value: a.value === null ? null : number(a.value, index) })),
    text: n.text === undefined ? undefined : number(n.text, index),
    children: n.children.map((c) => clone(c, index)),
  };
}

/** `$` vira o número da repetição (`$$` com zero à esquerda). */
function number(s: string, index: number) {
  return s.replace(/\$+/g, (m) => String(index).padStart(m.length, "0"));
}

/** O texto do usuário não pode virar campo do snippet por acidente. */
const literal = (s: string) => s.replace(/([$#])\{/g, "$1​{");

export interface ExpandOptions {
  jsx?: boolean;
  indent?: string;
}

/**
 * Expande a abreviação num snippet do CodeMirror (`${}` são os campos, na ordem do Tab), ou null
 * se o texto não é uma abreviação.
 */
export function expandAbbreviation(abbr: string, opts: ExpandOptions = {}): string | null {
  const text = abbr.trim();
  if (!text) return null;
  if (text === "!" || text === "html:5") return opts.jsx ? null : HTML5_DOCUMENT;
  let tree: Node[];
  try {
    tree = parse(text);
  } catch (err) {
    if (err instanceof ParseError) return null;
    throw err;
  }
  const indent = opts.indent ?? "\t";
  const classAttr = opts.jsx ? "className" : "class";
  let fields = 0;
  const field = () => `\${${++fields}}`;
  const render = (nodes: Node[], depth: number, numberFrom = 1): string[] => {
    const lines: string[] = [];
    for (const node of nodes) {
      for (let n = 0; n < node.count; n++) {
        const idx = node.count > 1 ? n + 1 : numberFrom;
        const el = node.count > 1 ? clone(node, idx) : node;
        const alias = ALIASES[el.tag];
        const tag = alias?.tag ?? el.tag;
        const pad = indent.repeat(depth);
        if (!tag) {
          lines.push(pad + literal(el.text ?? ""));
          continue;
        }
        const attrs: string[] = [];
        if (el.id) attrs.push(`id="${literal(el.id)}"`);
        if (el.classes.length) attrs.push(`${classAttr}="${literal(el.classes.join(" "))}"`);
        const given = new Set(el.attrs.map((a) => a.name));
        const defaults = (alias?.attrs ?? (el.attrs.length ? [] : (DEFAULT_ATTRS[tag] ?? []))).filter(
          (d) => !given.has(d.split("=")[0]!),
        );
        for (const d of defaults) {
          const [name, ...v] = d.split("=");
          attrs.push(v.length ? `${name}="${v.join("=")}"` : `${name}="${field()}"`);
        }
        for (const a of el.attrs)
          attrs.push(a.value === null ? `${a.name}="${field()}"` : `${a.name}="${literal(a.value)}"`);
        const open = `<${tag}${attrs.length ? " " + attrs.join(" ") : ""}`;
        if (VOID.has(tag)) {
          lines.push(pad + open + (opts.jsx ? " />" : ">"));
          continue;
        }
        if (el.children.length) {
          lines.push(pad + open + ">" + (el.text ? literal(el.text) : ""));
          lines.push(...render(el.children, depth + 1, idx));
          lines.push(pad + `</${tag}>`);
        } else {
          lines.push(pad + open + ">" + (el.text !== undefined ? literal(el.text) : field()) + `</${tag}>`);
        }
      }
    }
    return lines;
  };
  const out = render(tree, 0).join("\n");
  return fields ? out : `${out}\${0}`;
}

/** Abreviação no fim do texto (o que vem antes do cursor na linha), ou null. */
export function abbreviationBefore(textBefore: string): string | null {
  // Volta enquanto houver caracteres de abreviação; espaços só dentro de [] e {}.
  let depthBrace = 0;
  let depthBracket = 0;
  let i = textBefore.length;
  while (i > 0) {
    const c = textBefore[i - 1]!;
    if (c === "}") depthBrace++;
    else if (c === "{") {
      if (!depthBrace) break;
      depthBrace--;
    } else if (c === "]") depthBracket++;
    else if (c === "[") {
      if (!depthBracket) break;
      depthBracket--;
    } else if (!depthBrace && !depthBracket && !/[A-Za-z0-9_.#>+*^$@!:()\-=]/.test(c)) break;
    i--;
  }
  let abbr = textBefore.slice(i);
  if (!abbr || depthBrace || depthBracket) return null;
  // Logo depois de "<" ou "</" é o nome de uma tag sendo digitada; depois do ">" é conteúdo.
  if (i > 0 && /[</]/.test(textBefore[i - 1]!)) {
    const close = abbr.indexOf(">");
    if (close < 0) return null;
    abbr = abbr.slice(close + 1);
    if (!abbr) return null;
  }
  // Precisa começar como um elemento (não no meio de uma palavra com "<" antes etc.).
  if (!/^[A-Za-z.#!([{]/.test(abbr)) return null;
  return abbr;
}

// ───────────── CSS ─────────────

const CSS_PROPS: Record<string, string> = {
  m: "margin",
  mt: "margin-top",
  mr: "margin-right",
  mb: "margin-bottom",
  ml: "margin-left",
  p: "padding",
  pt: "padding-top",
  pr: "padding-right",
  pb: "padding-bottom",
  pl: "padding-left",
  w: "width",
  h: "height",
  maw: "max-width",
  mah: "max-height",
  miw: "min-width",
  mih: "min-height",
  fz: "font-size",
  fw: "font-weight",
  lh: "line-height",
  t: "top",
  r: "right",
  b: "bottom",
  l: "left",
  g: "gap",
  br: "border-radius",
  z: "z-index",
  op: "opacity",
};

const CSS_KEYWORDS: Record<string, string> = {
  df: "display: flex;",
  dg: "display: grid;",
  db: "display: block;",
  dib: "display: inline-block;",
  dn: "display: none;",
  fdc: "flex-direction: column;",
  fdr: "flex-direction: row;",
  fww: "flex-wrap: wrap;",
  jcc: "justify-content: center;",
  jcsb: "justify-content: space-between;",
  jcsa: "justify-content: space-around;",
  jcfs: "justify-content: flex-start;",
  jcfe: "justify-content: flex-end;",
  aic: "align-items: center;",
  ais: "align-items: stretch;",
  aifs: "align-items: flex-start;",
  aife: "align-items: flex-end;",
  tac: "text-align: center;",
  tar: "text-align: right;",
  tal: "text-align: left;",
  posa: "position: absolute;",
  posr: "position: relative;",
  posf: "position: fixed;",
  poss: "position: sticky;",
  ovh: "overflow: hidden;",
  ova: "overflow: auto;",
  curp: "cursor: pointer;",
  bxzbb: "box-sizing: border-box;",
  tdn: "text-decoration: none;",
  fwb: "font-weight: bold;",
  c: "color: ${1:#000};",
  bg: "background: ${1:#fff};",
  bgc: "background-color: ${1:#fff};",
  bd: "border: ${1:1px} solid ${2:#000};",
  trs: "transition: ${1:all} ${2:0.2s} ${3:ease};",
  gtc: "grid-template-columns: ${1:repeat(2, 1fr)};",
};

/** Abreviação de CSS (`m10`, `w100p`, `p10-20`, `df`, `jcc`…) em declaração, ou null. */
export function expandCss(abbr: string): string | null {
  const text = abbr.trim();
  if (CSS_KEYWORDS[text]) return CSS_KEYWORDS[text]!;
  const m = /^([a-z]+?)(-?\d*\.?\d+)(p|e|r|x)?((?:-\d*\.?\d+(?:p|e|r|x)?)*)$/.exec(text);
  if (!m) {
    const prop = CSS_PROPS[text];
    return prop ? `${prop}: \${1};` : null;
  }
  const prop = CSS_PROPS[m[1]!];
  if (!prop) return null;
  const unitless =
    prop === "z-index" || prop === "opacity" || prop === "font-weight" || prop === "line-height";
  const unit = (u: string | undefined) =>
    u === "p" ? "%" : u === "e" ? "em" : u === "r" ? "rem" : u === "x" ? "ex" : unitless ? "" : "px";
  const values = [`${m[2]}${m[2] === "0" ? "" : unit(m[3])}`];
  for (const extra of (m[4] ?? "").split("-").filter(Boolean)) {
    const em = /^(\d*\.?\d+)(p|e|r|x)?$/.exec(extra);
    if (em) values.push(`${em[1]}${em[1] === "0" ? "" : unit(em[2])}`);
  }
  return `${prop}: ${values.join(" ")};`;
}
